import { useState, useRef, useEffect } from 'react';
import { useStore } from '../context/ProductStore';
import { Layout, Image as ImageIcon, Save, RefreshCw, Layers, Monitor, GripVertical, Star, Search, CheckCircle2, Circle, Type, Trash2 } from 'lucide-react';
import { uploadImage } from '../utils/upload';
import './StorefrontEditor.css';

export default function StorefrontEditor() {
  const { 
    categories, products = [], featuredProductIds = [], 
    homepage, siteContent, 
    updateCategory, reorderCategories, updateHomepage, 
    saveSiteContent, resetHomepage, resetCategories, 
    getFullUrl, saveFeaturedProducts 
  } = useStore();

  const [activeTab, setActiveTab] = useState('categories');
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Local state for buffered edits
  const [localHomepage, setLocalHomepage] = useState(null);
  const [localSiteContent, setLocalSiteContent] = useState(null);

  // Sync local state when store data arrives
  useEffect(() => {
    if (homepage && !localHomepage) setLocalHomepage(JSON.parse(JSON.stringify(homepage)));
    if (siteContent && !localSiteContent) setLocalSiteContent(JSON.parse(JSON.stringify(siteContent)));
  }, [homepage, siteContent]);

  // Drag state
  const dragItem = useRef(null);
  const dragOver = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save homepage if it was edited
      if (localHomepage && activeTab === 'homepage') {
        await updateHomepage(localHomepage);
      }
      // Save site content if it was edited
      if (localSiteContent && activeTab === 'siteContent') {
        await saveSiteContent(localSiteContent);
      }
      // Categories are saved immediately in this UI, so nothing to do here unless we refactor them too
      
      await new Promise(r => setTimeout(r, 500));
      alert('Changes saved successfully!');
    } catch (err) {
      console.error("Save error:", err);
      alert('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, callback) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        alert('File too large. Please keep it under 50MB.');
        return;
      }
      try {
        const url = await uploadImage(file);
        callback(url);
      } catch (err) {
        console.error("Upload error:", err);
      }
    }
  };

  // ── Drag handlers ──
  const handleDragStart = (e, index, id) => {
    dragItem.current = index;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e, index, id) => {
    dragOver.current = index;
    setDragOverId(id);
  };

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) {
      setDraggingId(null);
      setDragOverId(null);
      dragItem.current = null;
      dragOver.current = null;
      return;
    }
    const reordered = [...categories];
    const [moved] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOver.current, 0, moved);
    await reorderCategories(reordered);
    setDraggingId(null);
    setDragOverId(null);
    dragItem.current = null;
    dragOver.current = null;
  };

  // Wait for data
  if (!localHomepage || !localSiteContent) {
    return <div className="admin-loading">Loading Editor...</div>;
  }

  return (
    <div className="store-editor">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Storefront Editor</h1>
          <p className="admin-page-sub">Customize your homepage banners and category images</p>
        </div>
        <div className="admin-header-actions">
          <button 
            className="admin-btn-secondary" 
            onClick={() => {
              if (window.confirm('Reset this tab to default settings?')) {
                if (activeTab === 'categories') resetCategories();
                else if (activeTab === 'homepage') {
                   resetHomepage();
                   setLocalHomepage(null); // Force resync
                }
              }
            }}
          >
            Reset Tab
          </button>
          <button 
            className={`admin-btn-primary ${saving ? 'loading' : ''}`} 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <RefreshCw size={16} className="spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="editor-tabs">
        <button 
          className={`editor-tab ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          <Layers size={16} /> Categories
        </button>
        <button 
          className={`editor-tab ${activeTab === 'featured' ? 'active' : ''}`}
          onClick={() => setActiveTab('featured')}
        >
          <Star size={16} /> Most Loved Products
        </button>
        <button 
          className={`editor-tab ${activeTab === 'homepage' ? 'active' : ''}`}
          onClick={() => setActiveTab('homepage')}
        >
          <Monitor size={16} /> Homepage Banners
        </button>
        <button 
          className={`editor-tab ${activeTab === 'siteContent' ? 'active' : ''}`}
          onClick={() => setActiveTab('siteContent')}
        >
          <Type size={16} /> Site Text Content
        </button>
      </div>

      <div className="editor-content">
        {/* ── MOST LOVED PRODUCTS PICKER ── */}
        {activeTab === 'featured' && (() => {
          const publishedProducts = products.filter(p => p.status === 'published');
          const filtered = productSearch.trim()
            ? publishedProducts.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase()))
            : publishedProducts;

          const toggle = async (pid) => {
            const sid = String(pid);
            const current = featuredProductIds.map(String);
            const next = current.includes(sid)
              ? current.filter(id => id !== sid)
              : [...current, sid];
            await saveFeaturedProducts(next);
          };

          return (
            <div>
              <div className="drag-hint" style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#c2410c', marginBottom: 20 }}>
                <Star size={14} />
                <span>Jinhe tick karein wahi products "Most Loved" carousel mein dikhenge · {featuredProductIds.length} selected</span>
              </div>

              <div className="featured-search-bar">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Product search karein..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                />
              </div>

              <div className="featured-products-grid">
                {filtered.map(p => {
                  const pid = String(p.id || p._id);
                  const isSelected = featuredProductIds.map(String).includes(pid);
                  return (
                    <div
                      key={pid}
                      className={`featured-product-card ${isSelected ? 'fp-selected' : ''}`}
                      onClick={() => toggle(pid)}
                    >
                      <div className="fp-check">
                        {isSelected ? <CheckCircle2 size={20} className="fp-check-on" /> : <Circle size={20} className="fp-check-off" />}
                      </div>
                      <img
                        src={p.image || p.images?.[0]?.url}
                        alt={p.name}
                        className="fp-img"
                        onError={e => { e.target.style.opacity = '0.3'; }}
                      />
                      <div className="fp-info">
                        <p className="fp-name">{p.name}</p>
                        <p className="fp-price">₹{p.price}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {activeTab === 'categories' && (
          <>
            <div className="drag-hint">
              <GripVertical size={14} />
              <span>Categories ko drag karke upar-niche reorder karein</span>
            </div>
            <div className="categories-editor-grid">
              {categories.map((cat, index) => (
                <div
                  key={cat.id}
                  className={`admin-card cat-edit-card ${draggingId === cat.id ? 'cat-dragging' : ''} ${dragOverId === cat.id && draggingId !== cat.id ? 'cat-drag-over' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index, cat.id)}
                  onDragEnter={(e) => handleDragEnter(e, index, cat.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnd={handleDragEnd}
                >
                  <div className="cat-edit-header">
                    <div className="drag-handle" title="Drag to reorder">
                      <GripVertical size={18} />
                    </div>
                    <span className="cat-edit-emoji">{cat.emoji}</span>
                    <div className="cat-edit-info">
                      <h4>{cat.name}</h4>
                      <p>{cat.slug}</p>
                    </div>
                    <span className="cat-order-badge">#{index + 1}</span>
                  </div>
                  
                  <div className="cat-edit-preview" style={{ backgroundImage: `url(${getFullUrl(cat.image)})` }}>
                    {!cat.image && <ImageIcon size={24} />}
                  </div>

                  <div className="admin-field">
                    <label>Category Image</label>
                    <div className="hp-input-group">
                      <input 
                        type="text" 
                        value={cat.image} 
                        onChange={(e) => updateCategory(cat.id, { image: e.target.value })}
                        placeholder="URL or Upload"
                      />
                      <label className="admin-upload-btn" title="Upload Image">
                        <ImageIcon size={16} />
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={(e) => handleFileUpload(e, (url) => updateCategory(cat.id, { image: url }))} 
                          style={{ display: 'none' }} 
                        />
                      </label>
                      {cat.image && (
                        <button 
                          className="admin-delete-btn" 
                          onClick={() => { if(window.confirm('Image remove karein?')) updateCategory(cat.id, { image: '' }) }}
                          title="Remove Image"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="admin-field">
                    <label>Display Name</label>
                    <input 
                      type="text" 
                      value={cat.name} 
                      onChange={(e) => updateCategory(cat.id, { name: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === 'homepage' && (
          <div className="homepage-editor">
            <div className="admin-card">
              <h3 className="card-title"><Monitor size={18} /> Hero Section</h3>
              <div className="hp-edit-grid">
                <div className="admin-field">
                  <label>Hero Background Image</label>
                  <div className="hp-input-group">
                    <input 
                      type="text" 
                      value={localHomepage.hero.bg} 
                      onChange={(e) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, bg: e.target.value } })}
                    />
                    <label className="admin-upload-btn" title="Upload Image">
                      <ImageIcon size={16} />
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, bg: url } }))} style={{ display: 'none' }} />
                    </label>
                    {localHomepage.hero.bg && (
                      <button 
                        className="admin-delete-btn" 
                        onClick={() => { if(window.confirm('Banner remove karein?')) setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, bg: '' } }) }}
                        title="Remove Image"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="hp-mini-prev" style={{ backgroundImage: `url(${getFullUrl(localHomepage.hero.bg)})` }} />
                  </div>
                </div>
                <div className="admin-field">
                  <label>Main Product Image</label>
                  <div className="hp-input-group">
                    <input 
                      type="text" 
                      value={localHomepage.hero.main} 
                      onChange={(e) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, main: e.target.value } })}
                    />
                    <label className="admin-upload-btn" title="Upload Image">
                      <ImageIcon size={16} />
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, main: url } }))} style={{ display: 'none' }} />
                    </label>
                    {localHomepage.hero.main && (
                      <button 
                        className="admin-delete-btn" 
                        onClick={() => { if(window.confirm('Image remove karein?')) setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, main: '' } }) }}
                        title="Remove Image"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="hp-mini-prev" style={{ backgroundImage: `url(${getFullUrl(localHomepage.hero.main)})` }} />
                  </div>
                </div>
                <div className="admin-field">
                  <label>Top Small Image</label>
                  <div className="hp-input-group">
                    <input 
                      type="text" 
                      value={localHomepage.hero.top} 
                      onChange={(e) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, top: e.target.value } })}
                    />
                    <label className="admin-upload-btn" title="Upload Image">
                      <ImageIcon size={16} />
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, top: url } }))} style={{ display: 'none' }} />
                    </label>
                    {localHomepage.hero.top && (
                      <button 
                        className="admin-delete-btn" 
                        onClick={() => { if(window.confirm('Image remove karein?')) setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, top: '' } }) }}
                        title="Remove Image"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="hp-mini-prev" style={{ backgroundImage: `url(${getFullUrl(localHomepage.hero.top)})` }} />
                  </div>
                </div>
                <div className="admin-field">
                  <label>Bottom Small Image</label>
                  <div className="hp-input-group">
                    <input 
                      type="text" 
                      value={localHomepage.hero.bot} 
                      onChange={(e) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, bot: e.target.value } })}
                    />
                    <label className="admin-upload-btn" title="Upload Image">
                      <ImageIcon size={16} />
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, bot: url } }))} style={{ display: 'none' }} />
                    </label>
                    {localHomepage.hero.bot && (
                      <button 
                        className="admin-delete-btn" 
                        onClick={() => { if(window.confirm('Image remove karein?')) setLocalHomepage({ ...localHomepage, hero: { ...localHomepage.hero, bot: '' } }) }}
                        title="Remove Image"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="hp-mini-prev" style={{ backgroundImage: `url(${getFullUrl(localHomepage.hero.bot)})` }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-card">
              <h3 className="card-title"><Monitor size={18} /> Promotional Banners</h3>
              <div className="hp-edit-grid">
                <div className="admin-field">
                  <label>Red Banner Image (Gift Boxes)</label>
                  <div className="hp-input-group">
                    <input 
                      type="text" 
                      value={localHomepage.promos.red} 
                      onChange={(e) => setLocalHomepage({ ...localHomepage, promos: { ...localHomepage.promos, red: e.target.value } })}
                    />
                    <label className="admin-upload-btn" title="Upload Image">
                      <ImageIcon size={16} />
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, promos: { ...localHomepage.promos, red: url } }))} style={{ display: 'none' }} />
                    </label>
                    {localHomepage.promos.red && (
                      <button 
                        className="admin-delete-btn" 
                        onClick={() => { if(window.confirm('Banner remove karein?')) setLocalHomepage({ ...localHomepage, promos: { ...localHomepage.promos, red: '' } }) }}
                        title="Remove Image"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="hp-mini-prev" style={{ backgroundImage: `url(${getFullUrl(localHomepage.promos.red)})` }} />
                  </div>
                </div>
                <div className="admin-field">
                  <label>Yellow Banner Image (Trail Mix)</label>
                  <div className="hp-input-group">
                    <input 
                      type="text" 
                      value={localHomepage.promos.yellow} 
                      onChange={(e) => setLocalHomepage({ ...localHomepage, promos: { ...localHomepage.promos, yellow: e.target.value } })}
                    />
                    <label className="admin-upload-btn" title="Upload Image">
                      <ImageIcon size={16} />
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, promos: { ...localHomepage.promos, yellow: url } }))} style={{ display: 'none' }} />
                    </label>
                    {localHomepage.promos.yellow && (
                      <button 
                        className="admin-delete-btn" 
                        onClick={() => { if(window.confirm('Banner remove karein?')) setLocalHomepage({ ...localHomepage, promos: { ...localHomepage.promos, yellow: '' } }) }}
                        title="Remove Image"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="hp-mini-prev" style={{ backgroundImage: `url(${getFullUrl(localHomepage.promos.yellow)})` }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-card">
              <h3 className="card-title"><Monitor size={18} /> Homepage Promo Video</h3>
              <div className="admin-field">
                <label>Promo Video (16:9 recommended)</label>
                <div className="hp-input-group">
                  <input 
                    type="text" 
                    value={localHomepage.video || ''} 
                    onChange={(e) => setLocalHomepage({ ...localHomepage, video: e.target.value })}
                    placeholder="Video URL or Upload"
                  />
                  <label className="admin-upload-btn" title="Upload Video">
                    <ImageIcon size={16} />
                    <input 
                      type="file" 
                      accept="video/*" 
                      onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, video: url }))} 
                      style={{ display: 'none' }} 
                    />
                  </label>
                </div>
                
                {localHomepage.video && (
                  <div className="video-editor-preview">
                    <div className="video-prev-box">
                      <video src={getFullUrl(localHomepage.video)} controls muted />
                    </div>
                    <div className="video-actions">
                      <label className="admin-btn-secondary btn-sm">
                        <RefreshCw size={14} /> Change Video
                        <input 
                          type="file" 
                          accept="video/*" 
                          onChange={(e) => handleFileUpload(e, (url) => setLocalHomepage({ ...localHomepage, video: url }))} 
                          style={{ display: 'none' }} 
                        />
                      </label>
                      <button 
                        className="admin-btn-danger btn-sm" 
                        onClick={() => {
                          if (window.confirm('Video remove karein?')) {
                            setLocalHomepage({ ...localHomepage, video: '' });
                          }
                        }}
                      >
                        <Trash2 size={14} /> Remove Video
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'siteContent' && (
          <div className="homepage-editor">
            <div className="admin-card">
              <h3 className="card-title"><Type size={18} /> About Us - Hero Section</h3>
              <div className="hp-edit-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="admin-field">
                  <label>Eyebrow Text</label>
                  <input 
                    type="text" 
                    value={localSiteContent.about.eyebrow} 
                    onChange={(e) => setLocalSiteContent({ ...localSiteContent, about: { ...localSiteContent.about, eyebrow: e.target.value } })}
                  />
                </div>
                <div className="admin-field">
                  <label>Main Title</label>
                  <input 
                    type="text" 
                    value={localSiteContent.about.title} 
                    onChange={(e) => setLocalSiteContent({ ...localSiteContent, about: { ...localSiteContent.about, title: e.target.value } })}
                  />
                </div>
                <div className="admin-field">
                  <label>Lead Paragraph</label>
                  <textarea 
                    rows={4}
                    value={localSiteContent.about.lead} 
                    onChange={(e) => setLocalSiteContent({ ...localSiteContent, about: { ...localSiteContent.about, lead: e.target.value } })}
                    className="admin-textarea"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="admin-field">
                    <label>Stat Number (e.g. 2M+)</label>
                    <input 
                      type="text" 
                      value={localSiteContent.about.statsNum} 
                      onChange={(e) => setLocalSiteContent({ ...localSiteContent, about: { ...localSiteContent.about, statsNum: e.target.value } })}
                    />
                  </div>
                  <div className="admin-field">
                    <label>Stat Label (e.g. Happy Customers)</label>
                    <input 
                      type="text" 
                      value={localSiteContent.about.statsLabel} 
                      onChange={(e) => setLocalSiteContent({ ...localSiteContent, about: { ...localSiteContent.about, statsLabel: e.target.value } })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-card">
              <h3 className="card-title"><Type size={18} /> About Us - Mission Section</h3>
              <div className="hp-edit-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="admin-field">
                  <label>Eyebrow</label>
                  <input 
                    type="text" 
                    value={localSiteContent.mission.eyebrow} 
                    onChange={(e) => setLocalSiteContent({ ...localSiteContent, mission: { ...localSiteContent.mission, eyebrow: e.target.value } })}
                  />
                </div>
                <div className="admin-field">
                  <label>Title</label>
                  <input 
                    type="text" 
                    value={localSiteContent.mission.title} 
                    onChange={(e) => setLocalSiteContent({ ...localSiteContent, mission: { ...localSiteContent.mission, title: e.target.value } })}
                  />
                </div>
                <div className="admin-field">
                  <label>Mission Text 1</label>
                  <textarea 
                    rows={4}
                    value={localSiteContent.mission.text1} 
                    onChange={(e) => setLocalSiteContent({ ...localSiteContent, mission: { ...localSiteContent.mission, text1: e.target.value } })}
                    className="admin-textarea"
                  />
                </div>
                <div className="admin-field">
                  <label>Mission Text 2</label>
                  <textarea 
                    rows={4}
                    value={localSiteContent.mission.text2} 
                    onChange={(e) => setLocalSiteContent({ ...localSiteContent, mission: { ...localSiteContent.mission, text2: e.target.value } })}
                    className="admin-textarea"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
